import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, signal, viewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AlertController, IonButton, IonButtons, IonContent, IonHeader, IonIcon, IonSpinner, IonTitle, IonToolbar, ModalController, ToastController } from '@ionic/angular/standalone';

import { BackupFitEstimate } from '../../sync/backup-fit.util';
import { BackupSpaceService } from '../../services/backup-space.service';
import { SyncService } from '../../sync/sync.service';
import { formatBytes } from '../../utils/format-bytes.util';
import { SyncMessage } from '../../../../../common/enums/sync-message.enum';
import { ProfileService } from '../../../../core/pages/settings/profile/profile.service';
import { NetworkService } from '../../../../../common/services/network/network.service';
import { formatDisplayDateTime } from '../../../../../common/utils/display-date.util';
import { BackupSummaryCardComponent } from './backup-summary-card.component';
import { CloudItemsSectionComponent } from './cloud-items-section.component';
import { CloudWipeCardComponent } from './cloud-wipe-card.component';
import { FailedBackupsSectionComponent } from './failed-backups-section.component';
import { confirmMeteredTransfer } from './metered-confirm.util';

/** How long the cloud items section stays highlighted after "Free up space"
 *  scrolls the user to it. Long enough to notice, short enough not to nag. */
const REVEAL_HIGHLIGHT_MS = 2000;

/**
 * Full-screen modal opened from the Settings → Cloud backup row. Backing up is
 * MANUAL (like WhatsApp): one button, one pass, no background sync, no retry
 * loop. The button sends what the cloud has not got - new items and saved
 * changes to items already there (ADR-0288, widening ADR-0261) - and content
 * comes back down one item at a time from the In the Cloud list — the
 * whole-library restore is gone.
 *
 * Everything about the cloud copy lives here, in the order the questions get
 * asked: how it stands, how much room is left, the button, what a backup does,
 * what is already up there (and how to delete or pull it back), and what
 * failed to go.
 */
@Component({
  selector: 'app-sync-sheet',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonSpinner,
    IonTitle,
    IonToolbar,
    BackupSummaryCardComponent,
    CloudItemsSectionComponent,
    CloudWipeCardComponent,
    FailedBackupsSectionComponent,
  ],
  template: `
    <ion-header>
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button (click)="close()" aria-label="Back">
            <ion-icon slot="icon-only" name="chevron-back"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Cloud backup</ion-title>
        <ion-buttons slot="end">
          <ion-button (click)="close()" aria-label="Close">
            <ion-icon slot="icon-only" name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <!-- How it stands and how much room is left: two halves of one glance, so
           they sit side by side wherever there is width for it. -->
      <div class="summary-grid">
        <!-- Status summary at the top of the sheet, mirrors the row. -->
        <div class="status-card surface">
          <div class="status-card__row">
            <ion-icon name="cloud-done-outline" color="primary" aria-hidden="true"></ion-icon>
            <div>
              <div class="status-card__label">Status</div>
              <div class="status-card__value">{{ statusLabel() }}</div>
            </div>
          </div>
          @if (lastSuccessAt(); as ts) {
            <div class="status-card__row">
              <ion-icon name="time-outline" color="medium" aria-hidden="true"></ion-icon>
              <div>
                <div class="status-card__label">Last backup</div>
                <div class="status-card__value">{{ formatDateTime(ts) }}</div>
              </div>
            </div>
          }
          @if (lastRestoreAt(); as ts) {
            <div class="status-card__row">
              <ion-icon name="cloud-download-outline" color="medium" aria-hidden="true"></ion-icon>
              <div>
                <div class="status-card__label">Last restore</div>
                <div class="status-card__value">{{ formatDateTime(ts) }}</div>
              </div>
            </div>
          }
          @if (lastError(); as err) {
            <div class="status-card__row status-card__row--error">
              <ion-icon name="warning-outline" color="danger" aria-hidden="true"></ion-icon>
              <div>
                <div class="status-card__label">Last error</div>
                <div class="status-card__value">{{ err }}</div>
              </div>
            </div>
          }
        </div>

        <!-- Authoritative cloud usage from the server. The bar is not decoration:
             "51 MB of 10 GB" takes a moment to place, and how full the account is
             is the one thing that decides whether the next backup will fit. -->
        @if (cloudSize(); as size) {
          <div class="status-card status-card--size surface">
            <div class="status-card__row">
              <ion-icon name="cloud-outline" color="primary" aria-hidden="true"></ion-icon>
              <div>
                <div class="status-card__label">Cloud size</div>
                <div class="status-card__value">{{ formatBytes(size.usedBytes) }} used</div>
              </div>
            </div>

            <div
              class="usage-bar"
              role="progressbar"
              [attr.aria-valuenow]="usagePercent()"
              aria-valuemin="0"
              aria-valuemax="100"
              [attr.aria-label]="'Cloud storage used'"
            >
              <span [class]="usageLevel()" [style.width.%]="Math.max(usagePercent(), 2)"></span>
            </div>

            <div class="usage-legend">
              <span>{{ usagePercent() }}% of {{ formatBytes(size.capBytes) }}</span>
              <span>{{ formatBytes(freeBytes()) }} free</span>
            </div>
          </div>
        }
      </div>

      <div class="sync-actions">
        <ion-button class="sync-now" [disabled]="syncing()" (click)="onBackUp()" data-testid="sync-backup-new">
          @if (syncing()) {
            <ion-spinner slot="start" name="crescent"></ion-spinner>
            {{ SyncMessage.StatusBackingUp }}
          } @else {
            <ion-icon slot="start" name="cloud-upload-outline"></ion-icon>
            {{ SyncMessage.BackUpButton }}
          }
        </ion-button>
      </div>

      <p class="manual-note muted">{{ SyncMessage.BackupManualNote }}</p>

      <!-- Only for a member of a business: inside one, backing up and restoring
           are also how work reaches a colleague, which is worth saying where the
           button is rather than in a help page. -->
      @if (inBusiness()) {
        <p class="manual-note muted" data-testid="sync-business-note">
          {{ SyncMessage.BusinessLibraryNote }}
        </p>
      }

      <!-- What the button does, said on the screen rather than only in a
           confirm: the question people bring here is whether backing up can
           take something away. -->
      <div class="status-card backup-scope surface">
        <div class="status-card__row">
          <ion-icon name="information-circle-outline" color="primary" aria-hidden="true"></ion-icon>
          <div>
            <div class="status-card__value">{{ SyncMessage.BackupAddsOnlyTitle }}</div>
            <p class="backup-scope__body">{{ SyncMessage.BackupAddsOnlyBody }}</p>
          </div>
        </div>
      </div>

      <app-backup-summary-card></app-backup-summary-card>

      <app-cloud-items-section
        [class.revealed]="revealed()"
        (changed)="onCloudChanged()"
      ></app-cloud-items-section>

      <app-failed-backups-section (changed)="onCloudChanged()"></app-failed-backups-section>

      <!-- Last, after everything it would destroy. -->
      <app-cloud-wipe-card></app-cloud-wipe-card>

      <!-- Clears the home indicator, which overlaps the bottom of the scroll
           area on a device that has one. A real element rather than padding on
           ion-content so it always counts toward scrollHeight. -->
      <div class="sheet-end" aria-hidden="true"></div>
    </ion-content>
  `,
  styles: [`
    /* The modal's .ion-page is a COLUMN FLEX container that expects ion-content
       as its child; here its child is this host, so the host has to be the flex
       container instead. As a plain block it gave ion-content no page height,
       ion-content sized to its own content, and the tail of the sheet rendered
       BELOW the modal — unreachable by scrolling and immune to any amount of
       padding, which is what two attempts at padding this failed to fix. */
    :host {
      display: flex;
      flex-direction: column;
      height: 100%;
      min-height: 0;
    }

    /* Padding is set here rather than with the ion-padding utility class so the
       sides and top stay at 16px while the bottom is owned by .sheet-end below. */
    ion-content {
      --padding-start: 16px;
      --padding-end: 16px;
      --padding-top: 16px;
      --padding-bottom: 0;
    }
    .sheet-end { height: calc(48px + var(--ion-safe-area-bottom, 0px)); }

    /* Two columns wherever they fit; one below, where a phone-width sheet would
       otherwise wrap both figures onto two lines each. Desktop-first to match
       the breakpoints the rest of the app uses. */
    .summary-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 14px;
      margin-bottom: 14px;
    }
    /* Free tier, or usage not loaded yet: no size card, so status takes the
       whole row instead of sitting in a half-width column beside nothing. */
    .summary-grid .status-card:only-child { grid-column: 1 / -1; }
    @media (max-width: 640px) {
      .summary-grid { grid-template-columns: 1fr; }
    }

    .status-card {
      padding: 14px;
      border-radius: 14px;
      display: grid;
      gap: 10px;
    }
    /* Its rows are a header, a bar and a legend rather than three stacked
       header/value pairs, so it spaces them out instead of clumping at the top. */
    .status-card--size { align-content: space-between; gap: 14px; }
    .usage-bar {
      height: 8px;
      border-radius: 4px;
      background: var(--app-border, rgba(255, 255, 255, 0.12));
      overflow: hidden;
    }
    .usage-bar > span {
      display: block;
      height: 100%;
      border-radius: inherit;
      background: var(--ion-color-primary);
      transition: width 240ms ease;
    }
    /* Colour is the earliest warning we can give — long before a backup fails
       for space, and without a number the user has to compare for themselves. */
    .usage-bar > span.high { background: var(--ion-color-warning); }
    .usage-bar > span.full { background: var(--ion-color-danger); }
    .usage-legend {
      display: flex;
      justify-content: space-between;
      gap: 10px;
      font-size: var(--app-fs-xs);
      color: var(--app-text-muted, rgba(0,0,0,0.55));
    }
    .status-card__row {
      display: flex;
      align-items: flex-start;
      gap: 12px;
    }
    .status-card__row ion-icon { font-size: 20px; margin-top: 2px; }
    .status-card__row--error .status-card__value { color: var(--ion-color-danger); }
    .status-card__label {
      font-size: var(--app-fs-xs);
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--app-text-muted, rgba(0,0,0,0.55));
    }
    .status-card__value { font-weight: 700; }

    /* The sheet's one action, full width. */
    .sync-actions {
      display: flex;
      gap: 10px;
      margin-block: 4px 14px;
    }
    .sync-actions ion-button {
      flex: 1 1 0;
      min-width: 0;
      margin: 0;
    }
    /* Let the label wrap rather than clip on a narrow sheet. */
    .sync-actions ion-button::part(native) { white-space: normal; }
    .manual-note { font-size: 13px; line-height: 1.4; margin: 0; }

    .backup-scope {
      margin-top: 14px;
      margin-bottom: 14px;
      border: 1px solid var(--ion-color-primary);
    }
    .backup-scope__body {
      margin: 4px 0 0;
      font-size: 13px;
      line-height: 1.4;
      color: var(--app-text-muted, rgba(0, 0, 0, 0.7));
    }
  `],
})
export class SyncSheetComponent {
  private readonly backupSpace = inject(BackupSpaceService);

  /** Shared app-wide date format (DD MMM YYYY, HH:mm) — not the `date` pipe's
   *  locale-dependent 'medium', which rendered US-first here. */
  protected readonly formatDateTime = formatDisplayDateTime;

  private readonly sync = inject(SyncService);
  private readonly modalCtrl = inject(ModalController);
  private readonly toastCtrl = inject(ToastController);
  private readonly alertCtrl = inject(AlertController);
  private readonly network = inject(NetworkService);
  private readonly profile = inject(ProfileService);

  /** Whether the signed-in account belongs to a business — read from the
   *  cached profile every signed-in surface has already fetched. */
  protected readonly inBusiness = toSignal(
    this.profile.currentUser$.pipe(map((user) => !!user?.organizationId)),
    { initialValue: false },
  );

  private readonly content = viewChild.required(IonContent);
  private readonly cloudSection = viewChild.required(CloudItemsSectionComponent);

  /** Briefly highlights the cloud items section after "Free up space". */
  protected readonly revealed = signal(false);

  protected readonly status = this.sync.status;
  protected readonly lastSuccessAt = this.sync.lastSuccessAt;
  protected readonly lastRestoreAt = this.sync.lastRestoreAt;
  protected readonly lastError = this.sync.lastError;
  protected readonly syncing = computed(() => this.sync.status() === 'syncing');

  /** Server-side storage usage for the Size section; null until loaded (section
   *  hidden). Shared with Settings so freeing space on one is visible on both. */
  protected readonly cloudSize = this.sync.storageUsage;
  protected readonly formatBytes = formatBytes;
  protected readonly Math = Math;

  /** Whole percent of the cap in use, clamped — a figure over 100 would be the
   *  server's to explain, not something to render as an overflowing bar. */
  protected readonly usagePercent = computed(() => {
    const size = this.cloudSize();
    if (!size?.capBytes) return 0;
    return Math.min(100, Math.round((size.usedBytes / size.capBytes) * 100));
  });

  /** What is left. Deliberately cap − used: `orphanBytes` is a SLICE of used, so
   *  crediting it here would show more free space than the account is billed for
   *  (only the pre-flight fit estimate, which runs before the sweep, may add it). */
  protected readonly freeBytes = computed(() => {
    const size = this.cloudSize();
    return Math.max(0, (size?.capBytes ?? 0) - (size?.usedBytes ?? 0));
  });

  /** Bar colour band. */
  protected readonly usageLevel = computed(() => {
    const pct = this.usagePercent();
    return pct >= 95 ? 'full' : pct >= 80 ? 'high' : '';
  });
  protected readonly SyncMessage = SyncMessage;

  constructor() {
    void this.sync.refreshStorageUsageQuietly();
  }

  protected readonly statusLabel = computed<string>(() => {
    switch (this.status()) {
      case 'syncing':  return SyncMessage.StatusBackingUp;
      case 'idle':     return 'Up to date';
      case 'offline':  return 'Offline';
      case 'error':    return 'Last backup failed';
      case 'disabled': return 'Disabled (free tier)';
      default:         return '—';
    }
  });

  protected close(): void {
    void this.modalCtrl.dismiss();
  }

  /** A section moved data in or out of the cloud. The summary above reads the
   *  same `cloudItems` signal the list does, so it re-renders on its own; only
   *  the restore path leaves the cloud list itself stale. */
  protected onCloudChanged(): void {
    void this.cloudSection().reload();
  }

  /** "Back up": new items and saved changes. Every question in the order it
   *  arises: offline, mobile data, guest items, cloud room, and whether to free
   *  the device copy. */
  protected async onBackUp(): Promise<void> {
    if (this.status() === 'offline') {
      await this.toast(SyncMessage.Offline, 'danger', 'cloud-offline-outline');
      return;
    }
    // On mobile data, confirm before spending the user's data plan (unless they
    // opted out). A backup can upload many media MB.
    if (!(await this.confirmMeteredIfNeeded())) return;
    // If this device has GUEST (unclaimed) content, let the user choose to upload it
    // into their account (claim) or keep it local-only before the backup runs.
    if (await this.sync.hasGuestEntities()) {
      const choice = await this.promptClaimGuest();
      if (choice === 'cancel') return;
      if (choice === 'upload') {
        try {
          await this.sync.claimAllGuestEntities();
        } catch (err) {
          await this.toast(`${SyncMessage.ClaimFailedPrefix} — ${err}`, 'danger', 'warning-outline');
          return;
        }
      }
    }
    // Pre-flight fit check: when not everything fits in the free cloud space,
    // say so and let the user choose — the run then backs up whole items that
    // fit (items are all-or-nothing; nothing is ever partially backed up).
    if (!(await this.confirmStorageFitIfNeeded())) return;
    // Last question before the run: upload only, or upload and stop holding the
    // media here. Silent when there is nothing to free.
    const choice = await this.backupSpace.ask(await this.sync.pendingItems());
    if (!choice) return;
    const ran = await this.sync.backUpPending();
    if (!ran) {
      // Nothing happened at all — reporting the previous run's outcome here is
      // how "Backup complete" gets shown for a backup that never started.
      await this.toast(SyncMessage.BackupFailedPrefix, 'danger', 'warning-outline');
      return;
    }
    void this.sync.refreshStorageUsageQuietly();
    // The run uploaded and swept orphans — every figure below the buttons has
    // moved. The summary re-renders with it, reading the same list.
    void this.cloudSection().reload();
    // Report the outcome honestly: failure, "nothing new to back up", or success.
    const err = this.sync.lastError();
    if (err) {
      // A storage-full line already leads with its own cause — don't bury it
      // behind the generic "Backup failed" prefix.
      const msg = err.startsWith(SyncMessage.StorageFull)
        ? err
        : `${SyncMessage.BackupFailedPrefix} — ${err}`;
      await this.toast(msg, 'danger', 'warning-outline');
    } else if (!this.sync.lastRunHadData()) {
      await this.toast(SyncMessage.BackupNothingNew, 'medium', 'cloud-offline-outline');
    } else {
      await this.toast(SyncMessage.BackupComplete, 'success', 'cloud-done-outline');
      await this.backupSpace.freeIfChosen(choice, [...this.sync.lastBackedUp()]);
    }
  }

  /**
   * When the pending backup won't fully fit in the free cloud space, show the
   * shortfall and ask before running — the run backs up whole items that fit;
   * the rest stay on this device. Fails open: no estimate (offline blip, usage
   * endpoint down) → just proceed, the run itself enforces the fit and reports
   * honestly at the end.
   *
   * The third button is the point of the dialog: telling someone they are out of
   * space without a way to free any is a dead end. The lever is the cloud items
   * list further down this same sheet, so the button scrolls to it rather than
   * closing the sheet for another screen — the user came here to back up, and
   * being ejected mid-flow to find their way back is what the section moving
   * into this sheet was meant to end.
   */
  private async confirmStorageFitIfNeeded(): Promise<boolean> {
    let est: BackupFitEstimate | null = null;
    try {
      est = await this.sync.estimateBackupFit({ pendingOnly: true });
    } catch { /* fail open */ }
    if (!est || est.neededBytes <= est.freeBytes) return true;
    const alert = await this.alertCtrl.create({
      header: SyncMessage.StorageFullHeader,
      message:
        `Your backup needs ${formatBytes(est.neededBytes)} but only ` +
        `${formatBytes(est.freeBytes)} of cloud storage is free. ` +
        `${est.itemsFitting} of ${est.itemsTotal} items fit and can be backed up ` +
        'now — the rest stay on this device until you free up space.',
      buttons: [
        { text: SyncMessage.Cancel, role: 'cancel' },
        { text: SyncMessage.StorageFullFreeUpSpace, role: 'free-up' },
        { text: SyncMessage.StorageFullBackUpWhatFits, role: 'confirm' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    if (role === 'free-up') {
      await this.revealCloudItems();
      return false;
    }
    return role === 'confirm';
  }

  /** Scroll the cloud items section into view and mark it, so a long sheet makes
   *  it obvious which part just answered the dialog. */
  private async revealCloudItems(): Promise<void> {
    await this.cloudSection().reload();
    const top = this.cloudSection().anchor().nativeElement.offsetTop;
    await this.content().scrollToPoint(0, Math.max(0, top - 8), 400);
    this.revealed.set(true);
    setTimeout(() => this.revealed.set(false), REVEAL_HIGHLIGHT_MS);
  }

  /** On mobile data, confirm before a backup spends the user's data plan. */
  private confirmMeteredIfNeeded(): Promise<boolean> {
    return confirmMeteredTransfer({ alertCtrl: this.alertCtrl, network: this.network }, 'backup');
  }

  /** Ask whether to upload guest items into the account, keep them local, or cancel. */
  private async promptClaimGuest(): Promise<'upload' | 'keep' | 'cancel'> {
    const alert = await this.alertCtrl.create({
      header: SyncMessage.ClaimGuestHeader,
      message: SyncMessage.ClaimGuestMessage,
      buttons: [
        { text: SyncMessage.ClaimGuestKeepLocal, role: 'keep' },
        { text: SyncMessage.ClaimGuestUpload, role: 'upload' },
        { text: SyncMessage.Cancel, role: 'cancel' },
      ],
    });
    await alert.present();
    const { role } = await alert.onDidDismiss();
    return role === 'upload' ? 'upload' : role === 'keep' ? 'keep' : 'cancel';
  }

  private async toast(message: string, color: string, icon: string): Promise<void> {
    const t = await this.toastCtrl.create({
      message,
      duration: color === 'danger' ? 4000 : 2500,
      position: 'bottom',
      color,
      icon,
    });
    await t.present();
  }
}
